import './SkinAnalysisReport.scss';

import { useState, useRef, useEffect } from 'react';
import { usePortContext } from '../../service/MessageChannelContext';
import { ScoreCarousel } from '../micro/ScoreCarousel/ScoreCarousel';
import { Card } from '../micro/Card/Card';
import { ScoreSummary } from '../micro/ScoreSummary/ScoreSummary';
import { getScoreKey } from '../../common/utils';
import { List } from '../micro/List/List';

import * as d3 from 'd3';

import Acne from '../../images/acne.png';
import Texture from '../../images/texture.png';
import PoreSize from '../../images/pores.png';
import DarkSpots from '../../images/spots.png';
import Brightness from '../../images/brightness.png';
import Reduce from '../../images/reduce.png';
import RightArrow from '../../images/right-arrow.png';
import AcneMask from '../../images/acne-mask.png';
import TextureMask from '../../images/texture-mask.png';
import PoreSizeMask from '../../images/pore-mask.png';
import DarkSpotsMask from '../../images/spots-mask.png';
import BrightnessMask from '../../images/brightness-mask.png';

import { Button } from '../micro/Button/Button';
import { useNavigate } from 'react-router-dom';
import { detectorPromise } from '../../common/utils';
import { Navbar } from '../micro/NavBar/NavBar';

const imageMap = {
  acne: Acne,
  texture: Texture,
  poreSize: PoreSize,
  darkSpots: DarkSpots,
  brightness: Brightness,
};

const maskMap = {
  acne: AcneMask,
  texture: TextureMask,
  poreSize: PoreSizeMask,
  darkSpots: DarkSpotsMask,
  brightness: BrightnessMask,
};

const content = {
  reportTitle: {
    en: 'Your Skintuation',
    id: 'Skintuation Kamu',
  },
  skinHealth: {
    en: 'Skin Health',
    id: 'Kesehatan Kulit',
  },
  acne: {
    en: 'Acne',
    id: 'Jerawat',
  },
  texture: {
    en: 'Texture',
    id: 'Tekstur',
  },
  poreSize: {
    en: 'Pore Size',
    id: 'Ukuran Pori',
  },
  darkSpots: {
    en: 'Dark Spots',
    id: 'Dark Spots',
  },
  brightness: {
    en: 'Brightness',
    id: 'Kulit Kemerahan',
  },
  scoreComment: {
    skinHealth: {
      canBeImproved: {
        en: "Own your skin journey and embrace your unique beauty! You're not alone - seek support and keep caring for yourself. You've got this!",
        id: 'Kamu pasti bisa! Nggak masalah punya masalah kulit. Yang penting, rawat dan percaya diri dengan kecantikan alamimu. Kamu kuat dan luar biasa!',
      },
      moderate: {
        en: "You're rocking it! Keep in mind, dedication pays off on your journey to flawless skin. Embrace your uniqueness and keep pushing forward with style!",
        id: 'Kamu luar biasa! Ingat, dedikasimu tidak akan mengecewakan dalam meraih kulit impianmu. Teruslah percaya diri dengan keunikanmu!',
      },
      isGood: {
        en: "You're fantastic! Your healthy skin is a reflection of your great care. Stay confident, because you're unique and worthy of love and admiration!",
        id: 'Keren banget! Kesehatan kulitmu tercermin dari perawatan skincare yang kamu lakukan. Stay confident, karena kamu unik dan worthy of love and admiration!',
      },
      isExcellent: {
        en: "You're a superstar! Your excellent healthy skin is enviable and shows your dedication. Keep caring for yourself and embrace your natural beauty, because you're truly amazing!",
        id: 'Wow! Kulit sehatmu yang sempurna menunjukkan dedikasimu. Terus rawat kulitmu dengan rajin skincare-an yaa bestie, keren banget!',
      },
    },
    acne: {
      canBeImproved: {
        en: "No worries, your acne will fade away with time and proper care. Embrace your uniqueness; you're more than just skin deep! Stay positive, you've totally got this!",
        id: 'Santai saja. Jerawatmu akan semakin membaik dengan rutin skincare. Cintai keunikanmu dan tetap positif, kamu pasti bisa mengatasi ini!',
      },
      moderate: {
        en: "No worries, everything's a work in progress, and acne doesn't define you. Stick to your skincare routine, and remember, you're not alone on this journey!",
        id: 'Tenang saja, semua ada prosesnya. Tetap jalankan skincare routine-mu, karena jerawat nggak menentukan siapa dirimu. Kamu nggak sendirian dalam perjalanan ini!',
      },
      isGood: {
        en: "Your skin is beautiful and unique, a few spots don't change that. Remember, it's normal! Embrace your natural beauty, you're amazing just the way you are!",
        id: "Kulitmu itu cantik dan unik—sedikit jerawat gak akan mengubahnya. Ingat, jerawatan itu wajar! Tetap pede sama kecantikan alamimu, you're doing great bestie!",
      },
      isExcellent: {
        en: "You're absolutely glowing! A little acne is no big deal because your beauty shines through. Keep up your brilliant skincare routine!",
        id: "You're absolutely glowing! Sedikit jerawat gak jadi masalah lagi untuk kulitmu. Terus lakuin skincare routine-mu yaa!",
      },
    },
    texture: {
      canBeImproved: {
        en: "Get ready for that silky smooth skin. Keep up with your skincare routine! Believe in yourself, because you've got the power to achieve anything!",
        id: 'Kulitmu bakal jadi halus banget kalau kamu konsisten dengan skincare routine-mu. Yakin pada dirimu dan terus semangat, bestie!',
      },
      moderate: {
        en: "Stay positive and keep your spirits high. Your worth goes beyond your skin texture. Keep pampering yourself and embrace your unique beauty. Remember, you're beautiful inside and out!",
        id: 'Sudah bagus kok. Tetap positif dan percaya diri dengan tekstur kulitmu, ya. Jangan lupa rawat kulitmu biar makin smooth!',
      },
      isGood: {
        en: "You're glowing! Your smooth skin is beautiful and you deserve to feel confident. Keep shining bright, you're one of a kind!",
        id: "You're glowing! Kulit kamu bagus dan smooth loh, jadi kamu berhak buat ngerasa pede! Keep shining bright bestie!",
      },
      isExcellent: {
        en: "You're a star! Your very smooth skin is a reward for your effort. Remember that you're special, so keep shining!",
        id: 'Keren banget! Kulitmu yang halus adalah hasil dari skincare routine-mu loh bestie. Be confident and keep on shining!',
      },
    },
    poreSize: {
      canBeImproved: {
        en: "Visible pores? No biggie! They're just part of your natural beauty. Stick to your skincare routine and strut your stuff with confidence. You're absolutely fabulous!",
        id: 'Pori-pori terlihat itu wajar banget, bestie! Tetap semangat dalam skincare-mu, kamu luar biasa!',
      },
      moderate: {
        en: "Visible pores are completely natural, and you're nailing it with your skincare routine. Stay confident and keep pampering your skin. You're truly extraordinary!",
        id: 'Pori-pori terlihat itu wajar banget, bestie! Tetap semangat dalam skincare-mu, kamu luar biasa!',
      },
      isGood: {
        en: "Your very slightly visible pores are a part of your beauty. Just keep caring for yourself and remember, you're special. Stay confident!",
        id: 'Wah, perawatan pori-pori wajahmu udah oke loh. Tetep rawat kulitmu and stay confident yaa bestie!',
      },
      isExcellent: {
        en: "You're incredible! Your barely visible pores are a sign of great skin. Keep caring for yourself and stay confident!",
        id: "You're incredible! Pori-pori yang kecil adalah tanda kulit yang bagus loh. Terus rawat kulitmu dan tetap pede yaa bestie!",
      },
    },
    darkSpots: {
      canBeImproved: {
        en: "Don't let dark spots define you, let your beauty shine bright. Keep pampering yourself and celebrate your uniqueness. You're amazing just as you are!",
        id: 'Tenang aja, dark spots nggak akan mengubah siapa dirimu! Tetap rawat kulitmu dan percaya diri dengan keunikan yang ada. Kamu luar biasa seperti dirimu yang sekarang!',
      },
      moderate: {
        en: "You're amazing! Don't let dark spots define you. Continue to care for yourself and celebrate your uniqueness. Stay confident because you're truly remarkable just as you are!",
        id: 'Kulitmu itu cantik dan unik, bintik hitam gak akan mengubahnya. Tetap percaya diri dan rajin rawat kulitmu, bestie. Kamu luar biasa apa adanya!',
      },
      isGood: {
        en: "You're shining bright! The minimal dark spots you have just add uniqueness to your face! Keep embracing your beauty, inside and out!",
        id: 'Kamu keren! Bintik hitam yang cuma sedikit itu bikin kamu terlihat unik loh! Tetap pede dengan kulitmu yaa!',
      },
      isExcellent: {
        en: "You're a star! Very few dark spots are a sign that you're good at taking care of your skin. Embrace your uniqueness and remember, you're amazing just the way you are!",
        id: 'Great job bestie! Bintik hitam yang sedikit artinya kamu pintar merawat kulitmu. Tetap percaya diri dan rajin skincare-an yaa!',
      },
    },
    brightness: {
      canBeImproved: {
        en: 'No worries, a bit of dullness is just a temporary phase and your radiance will bounce back! Celebrate your one-of-a-kind beauty and invest in self-care. Your brilliance is your best asset!',
        id: 'Santai aja, kulit kusam itu cuma tamu sebentar, tapi kulit cerahmu akan kembali! Terus rawat kulitmu dengan baik ya, bestie!',
      },
      moderate: {
        en: "Your skin's current dullness is just a passing phase. Keep pampering yourself because your radiance is on its way back. Get ready to shine bright!",
        id: 'Kulit yang sedikit kusam itu normal dan hanya sementara. Tetap rawat dirimu karena kilauanmu akan kembali. Kamu akan bersinar cerah, bestie!',
      },
      isGood: {
        en: "You're glowing! Your bright skin is stunning and it radiates beauty. Keep shining, you're truly remarkable!",
        id: "You're skin is glowing! Kondisi kulit kamu cerah dan bagus loh! Good job yaa bestie!",
      },
      isExcellent: {
        en: "You're glowing! Your bright skin is stunning and it radiates your beauty! Keep shining, you're truly a one-of-a-kind!",
        id: "You're super glowing! Kulit kamu terlihat sehat dan cerah banget. Keep on shining and be confident with your skin bestie!",
      },
    },
  },
  focusAreasTitle: {
    en: 'Your Skintuation',
    id: 'Skintuation Kamu',
  },
  focusAreas: {
    acne: {
      en: 'Excess sebum leads to clogged follicles, fostering acne bacteria growth, causing inflammation and forming pimples, blackheads, and whiteheads.',
      id: 'Sebum berlebih dapat menyumbat folikel, meningkatkan pertumbuhan bakteri jerawat, menyebabkan peradangan serta membentuk jerawat, komedo, dan whitehead.',
    },
    texture: {
      en: 'Rough skin results from dead cells, poor exfoliation, and reduced collagen. Environmental factors like sun exposure and pollution worsen elasticity and texture.',
      id: 'Kulit kasar disebabkan sel kulit mati, eksfoliasi yang kurang baik, serta penurunan kolagen. Faktor lingkungan seperti paparan matahari juga polusi memperburuk elastisitas dan tekstur kulitmu.',
    },
    poreSize: {
      en: 'Genetics and excess sebum affect large pore size. Aging, reduced elasticity, sun damage, and inflammation also contribute to pore enlargement.',
      id: 'Gen dan sebum berlebih mempengaruhi ukuran pori-pori yang besar. Penuaan, penurunan elasitisitas, kerusakan sinar matahari, serta peradangan menambah pembesaran ukuran pori-pori.',
    },
    darkSpots: {
      en: 'Dark spots form from excess melanin due to sun exposure, hormones, injuries, or inflammation. Acne, aging, and skin conditions can also cause hyperpigmentation.',
      id: 'Bintik hitam terbentuk dari melanin berlebih dari paparan sinar matahari, hormon, luka, atau peradangan. Jerawat, penuaan, dan kondisi kulit juga dapat menyebabkan hiperpigmentasi.',
    },
    brightness: {
      en: 'Dullness arises from factors like insufficient exfoliation, dead skin buildup, dehydration, and low collagen. Sun damage, pollution, stress, and lifestyle add to diminished radiance.',
      id: 'Kulit kusam disebabkan beragam faktor seperti eksfoliasi yang tidak cukup, penumpukan sel kulit mati, dehidrasi, penurunan kolagen, kerusakan sinar matahari, polusi, stres, juga gaya hidup.',
    },
  },

  areasToMaintainTitle: {
    en: 'Maintain This Aspect',
    id: 'Pertahankan Aspek Ini',
  },
  areasToMaintain: {
    acne: {
      en: 'Congratulation on handling acne well! Your commitment to skincare is commendable. Stay consistent for healthier, clearer skin—more positive changes ahead!',
      id: 'Keren! Kamu berhasil atasi jerawat dengan baik! Komitmenmu untuk skincare-an oke banget. Tetap konsisten buat kulitmu yang lebih sehat dan bersih yaa!',
    },
    texture: {
      en: 'Your skin care dedication is admirable! Keep exfoliating, moisturizing, and protecting it—the results will show, leading to smoother, radiant skin. Well done!',
      id: 'Usaha kamu dalam merawat kulit, keren banget! Rutin eksfoliasi, pakai pelembab, dan lindungi kulitmu—wajahmu akan lebih halus dan bercahaya. Well done!',
    },
    poreSize: {
      en: 'Your pore care is fantastic! Keep cleaning and using suitable products with consistency for minimized pores and healthier, refined skin. Well done!',
      id: 'Perawatan pori-porimu OK banget! Tetep konsisten bersihkan muka dan pakai produk yang cocok biar pori-porimu mengecil dan kulitmu jadi lebih sehat. Well done!',
    },
    darkSpots: {
      en: 'Impressive dark spot care! Use the right products and sun protection, be patient—efforts will pay off with improved skin. Keep shining!',
      id: 'Perawatan bintik hitammu OK banget! Pakai produk yang tepat dan lindungi kulit dari sinar matahari, untuk kondisi kulit yang lebih baik yaa. Keep shining, Bestie!',
    },
    brightness: {
      en: 'Impressive skin brightness care! Regular cleansing, exfoliating, and moisturizing are outstanding. Consistency will bring a brighter, radiant complexion. Glowing skin awaits!',
      id: 'Perawatan kulit kusam-mu oke nih, Bestie! Kamu rutin membersihkan wajah, eksfoliasi, dan pakai pelembap. Tetap konsisten supaya kulitmu makin cerah. Kulit yang glowing sudah menanti loh!',
    },
  },
  causeHeadings: {
    acne: {
      en: 'What causes acne?',
      id: 'Apa yang menyebabkan jerawat?',
    },
    texture: {
      en: 'What affects skin texture?',
      id: 'Apa yang mempengaruhi tekstur kulit?',
    },
    poreSize: {
      en: 'What causes large pores?',
      id: 'Apa yang menyebabkan pori-pori besar?',
    },
    darkSpots: {
      en: 'What causes dark spots?',
      id: 'Apa yang menyebabkan bintik hitam?',
    },
    brightness: {
      en: 'What causes dull skin?',
      id: 'Apa yang menyebabkan kulit kusam?',
    },
  },
  causes: {
    acne: {
      en: [
        'Acne happens when hair follicles get clogged with dead skin cells and oil.',
        'Hormonal changes and genetics can lead to acne.',
        'Keep your face clean with a gentle cleanser, avoid touching, and use non-comedogenic products.',
        "Don't worry, with the right care, we can see the improvements!",
      ],
      id: [
        'Jerawat muncul saat folikel rambut tersumbat sel-sel kulit mati dan minyak.',
        'Perubahan hormon dan genetik bisa menyebabkan jerawat, lho!',
        'Jaga kebersihan wajah dengan gentle cleanser, hindari menyentuh wajah, dan pakai produk yang non-comedogenic.',
        "Don't worry, dengan perawatan yang tepat, kamu bisa kok melihat perubahannya!",
      ],
    },
    texture: {
      en: [
        "Rough skin texture happens when dead skin cells build up aren't shed properly.",
        'Sun exposure, pollution, as well as not moisturizing enough can worsen roughness!',
        "Try gentle exfoliation using a scrub or a soft brush, don't forget to moisturize daily, do regular cleansing and protect from the sun.",
        'Do it all and it will help you achieve a smoother complexion.',
      ],
      id: [
        'Tekstur kulit yang kasar terjadi saat sel kulit mati yang menumpuk tidak terangkat dengan baik.',
        'Paparan sinar matahari, polusi, juga kurangnya moisturizer bisa memperburuk tekstur kulitmu!',
        'Lakukan eksfoliasi ringan dengan scrub atau brush yang lembut, pakai moisturizer, rutin membersihkan wajah, dan lindungi dari sinar matahari.',
        'Hal-hal di atas akan membantumu mendapatkan kulit wajah yang lebih halus!',
      ],
    },
    poreSize: {
      en: [
        "Large pore size refers to visible and enlarged openings on the skin's surface.",
        'Genetic and hormonal changes can play a role in your pores size!',
        "Sun exposure also worsens it by damaging the skin's collagen and elastin.",
        'Cleanse your face regularly, use non-comedogenic products, and protect from the sun will help minimize the appearance of large pores.',
      ],
      id: [
        'Ukuran pori-pori yang besar mengacu pada bukaan pori yang terlihat di wajah.',
        'Genetik dan perubahan hormon bisa berpengaruh terhadap ukuran pori-porimu loh!',
        'Paparan sinar matahari juga bisa memperburuk, karena merusak kolagen dan elastin di kulit.',
        'Rutin bersihkan wajah, gunakan produk non-comedogenic, dan lindungi kulitmu dari sinar matahari akan membantu meminimalisir pori-pori yang terlihat.',
      ],
    },
    darkSpots: {
      en: [
        'Dark spots (hyperpigmentation) is caused by excess production of melanin.',
        'Sun, acne, skin injuries, hormonal changes, picking pimples, all of it may also cause and leave dark marks.',
        'Avoiding the sun, using sunscreen, and not picking at your skin can help prevent dark spots!',
        'Treatments like topical creams with ingredients like retinoids or vitamin C may fade them over time.',
      ],
      id: [
        'Bintik hitam (hiperpigmentasi) terjadi karena produksi melanin yang berlebihan.',
        'Sinar matahari, jerawat, luka pada kulit, perubahan hormon, memencet jerawat, semuanya bisa menyebabkan dan meninggalkan bintik hitam.',
        'Jauhi sinar matahari, pakai sunscreen, dan tidak memencet kulit bisa menghindari timbulnya bintik hitam!',
        'Perawatan pakai krim topikal seperti retinoid atau vitamin C bisa memudarkannya seiring waktu.',
      ],
    },
    brightness: {
      en: [
        'Dull skin happens when dead skin cells build up on the surface, blocking light reflection.',
        'Not exfoliating regularly, dehydration, pollution, or harsh weather can contribute to dullness.',
        "Maintaining a balanced diet, staying hydrated, and getting enough sleep can help improve your skin's radiance.",
        'Keep your skin looking fresh by using a gentle exfoliator 1-2 times a week and moisturizing daily.',
      ],
      id: [
        'Kulit kusam terjadi ketika sel kulit mati menumpuk di permukaan kulit dan menghalangi pantulan cahaya.',
        'Jarang eksfoliasi, dehidrasi, serta polusi atau cuaca buruk bisa menambah kekusaman kulit.',
        'Asupan gizi yang seimbang, tetap terhidrasi, dan tidur cukup bisa meningkatkan kecerahan kulit.',
        'Jaga kulitmu agar tetap segar dengan eksfoliasi 1-2 kali seminggu serta lempabkan wajahmu setiap hari ya!',
      ],
    },
  },
  improvementHeadings: {
    acne: {
      en: 'How to reduce acne?',
      id: 'Bagaimana cara mengurangi jerawat?',
    },
    texture: {
      en: 'How to improve skin texture?',
      id: 'Bagaimana cara meningkatkan tekstur kulit?',
    },
    poreSize: {
      en: 'How to minimize pore size?',
      id: 'Bagaimana cara meminimalkan ukuran pori-pori?',
    },
    darkSpots: {
      en: 'How to get rid of dark spots?',
      id: 'Bagaimana cara menghilangkan bintik hitam?',
    },
    brightness: {
      en: 'How to get glowing skin?',
      id: 'Bagaimana cara mendapatkan kulit yang cerah?',
    },
  },
  improvements: {
    acne: {
      en: [
        'Cleanse gently: Wash your face twice daily with a mild cleanser.',
        'Hands off: Resist picking or touching your face to avoid spreading bacteria.',
        'Spot treat: Use acne-fighting products with benzoyl peroxide or salicylic acid on individual breakouts.',
        'Moisturize: Choose an oil-free, non-comedogenic moisturizer to keep your skin hydrated.',
        'Healthy habits: Eat a balanced diet, drink plenty of water, and manage stress to support overall skin health.',
      ],
      id: [
        'Gentle Cleansing: Bersihkan wajahmu 2x sehari dengan cleanser ringan.',
        'Jangan Sentuh Wajah: Hindari menekan atau menyentuh wajahmu agar bakteri tidak menyebar.',
        'Spot treat: Pakai produk anti acne yang mengandung benzoyl peroxide atau salicylic acid di titik breakoutsmu',
        'Moisturize: Pilih moisturizer yang bebas minyak dan non-comedogenic agar kulitmu tetap terhidrasi.',
        'Lakukan Kebiasaan Sehat: Konsumsi gizi seimbang, minum banyak air, dan kelola stresmu agar kesehatan kulitmu semakin baik!',
      ],
    },
    texture: {
      en: [
        'Regular cleansing: Cleanse your face twice daily with a gentle face wash.',
        'Exfoliate weekly: Use a scrub or chemical exfoliant to smoothen your skin.',
        'Hydrate well: Moisturize daily with a non-greasy with oil-free lotion to keep a supple skin.',
        'Sun protection: Wear sunscreen daily to shield skin from harmful UV ray.',
        'Healthy lifestyle: Eat nutritious foods, get enough sleep, and manage stress for a better skin texture.',
      ],
      id: [
        'Rutin Cleansing: Bersihkan wajahmu 2x sehari dengan face wash lembut.',
        'Eksfoliasi Mingguan: Gunakan scrub atau exfolian kimia agar kulitmu makin halus.',
        'Kulit Terhidrasi: Pakai moisturizer setiap hari dengan lotion bebas minyak agar kulit tetap elastis.',
        'Lindungi dari Matahari: Pakai sunscreen setiap hari untuk menghindari sinar UV yaa!',
        'Gaya Hidup Sehat: Konsumsi makanan bernutrisi, tidur cukup, dan kelola stresmu agar tekstur kulitmu semakin baik!',
      ],
    },
    poreSize: {
      en: [
        'Gentle cleansing: Wash your face twice daily with a mild cleanser.',
        'Exfoliate wisely: Use a gentle exfoliator once a week.',
        'Non-comedogenic products: Choose oil-free, pore-friendly skincare and makeup products.',
        'Sunscreen protection: Wear sunscreen daily to shield skin from UV rays and prevent further damage to pores.',
        "Patience and consistency: Stick to your routine and don't get discouraged!",
      ],
      id: [
        'Gentle Cleansing: Bersihkan wajahmu 2x sehari dengan cleanser ringan.',
        'Eksfoliasi Secukupnya: Gunakan eksfoliator lembut sekali seminggu.',
        'Gunakan Produk Non-comedogenic: Pilih produk skincare dan makeup yang bebas minyak dan ramah pori-pori.',
        'Pakai Sunscreen: Gunakan sunscreen setiap hari untuk menghindari sinar UV yaa!',
        'Bersabar dan Konsisten: Tetap rajin skincare-an dengan rutin ya, Bestie!',
      ],
    },
    darkSpots: {
      en: [
        'Sunscreen always: Protect your skin from the damaging sun.',
        'Fade creams: Use products like vitamin C or niacinamide to lighten dark spots.',
        'Patience is key: Dark spots take time to fade, so give your skincare routine some time.',
        'Avoid picking: Refrain from picking pimples to prevent dark marks.',
        'Consult a pro: If dark spots persist, seek advice from a dermatologist.',
      ],
      id: [
        'Pakai Sunscreen: Lindungi kulitmu dari sinar matahari yaa!',
        'Gunakan Krim Pemudar: Pakai produk yang mengandung vitamin C atau niacinamide untuk mencerahkan bintik hitam.',
        'Sabar adalah Kunci: Butuh waktu buat memudarkan bintik hitam, jadi biarkan skincare routine-mu bekerja yaa.',
        'Hindari Menekan Jerawat: Jangan menekan jerawatmu agar tidak meninggalkan bekas hitam.',
        'Konsultasi: Jika bintik hitam tetap muncul, konsultasikan dengan dermatologis.',
      ],
    },
    brightness: {
      en: [
        'Cleanse & exfoliate: Regularly cleanse your face and exfoliate for a brighter complexion!',
        'Hydration matters: Moisturize daily to keep your skin plump and glow naturally.',
        'Sunscreen protection: Use sunscreen to avoid the dangers of UV rays.',
        'Healthy diet: Eat fruits, veggies, and antioxidants to nourish your skin and promote radiance.',
        'Beauty Sleep: Get enough sleep every night so your skin can regenerate properly.',
      ],
      id: [
        'Bersihkan Wajah & Eksfoliasi: Rutin bersihkan dan eksfoliasi wajahmu biar kulit lebih cerah, ya!',
        'Moisturize: Jangan lupa pakai pelembap setiap hari biar kul',
        'Pakai Sunscreen: Gunakan sunscreen untuk hindari bahaya sinar UV.',
        'Makan Sehat: Konsumsi buah, sayur, serta antioksidan yang merawat kesehatan serta mencerahkan kulitmu.',
        'Beauty Sleep: Tidur cukup setiap malam agar kulitmu bisa beregenerasi dengan baik!',
      ],
    },
  },
  productRecommendation: {
    en: 'See Product Recommendations',
    id: 'Lihat Rekomendasi Produk',
  },
};

const TEXTURE_KEYPOINTS = [[197, 196, 217, 119, 228, 117, 207, 203, 79, 1, 309, 423, 427, 346, 448, 348, 437, 419, 197]];
const PORE_SIZE_KEYPOINTS = [
  [297, 293, 334, 296, 285, 417, 399, 420, 360, 1, 131, 198, 174, 193, 55, 66, 105, 63, 67, 297],
  [118, 119, 121, 47, 203, 206, 207, 187, 117],
  [347, 348, 350, 277, 423, 426, 427, 411, 346],
];
const DARK_SPOTS_KEYPOINTS = [
  [297, 293, 334, 296, 285, 417, 399, 420, 360, 1, 131, 198, 174, 193, 55, 66, 105, 63, 67, 297],
  [118, 119, 121, 47, 203, 206, 207, 187, 117],
  [347, 348, 350, 277, 423, 426, 427, 411, 346],
];

const BRIGHTNESS_KEYPOINTS = [
  [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93,
    234, 127, 162, 21, 54, 103, 67, 109, 10,
  ],
];

const keypointsMap = {
  skinHealth: [],
  acne: [],
  texture: TEXTURE_KEYPOINTS,
  poreSize: PORE_SIZE_KEYPOINTS,
  darkSpots: DARK_SPOTS_KEYPOINTS,
  brightness: BRIGHTNESS_KEYPOINTS,
};

const SkinCompleteDetail = ({ report }) => {
  const [selectedHealth, setSelectedHealth] = useState('skinHealth');
  const {
    isLoggedIn,
    analysisReport,
    lang,
    setAnalysisReport,
    isDebug,
    mode,
    events: { emitGetHistoryEvent, emitLoginEvent },
  } = usePortContext();

  const faceCanvasRef = useRef(null);
  /**
   * @type {[faceLandmarksDetection.Keypoint[], React.Dispatch<React.SetStateAction<faceLandmarksDetection.Keypoint[]>>]
   */
  const [keypoints, setKeypoints] = useState([]);
  const [logData, setLogData] = useState({});

  const navigate = useNavigate();

  if (!report) {
    report = analysisReport;
  }

  const focusAreaItems = report?.focusAreas?.map((area) => {
    return {
      title: content[area][lang],
      description: content.focusAreas[area][lang],
      image: imageMap[area],
    };
  });

  const maintainAreaItems = report?.areasToMaintain?.map((area) => {
    return {
      title: content[area][lang],
      description: content.areasToMaintain[area][lang],
      image: imageMap[area],
    };
  });

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.src = report?.imageUrls.skinHealth;

    image.onload = async () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = Math.min(window.innerWidth, 0.7 * window.innerHeight);
      canvas.height = 0.7 * window.innerHeight;

      const imageAspectRatio = image.width / image.height;
      const canvasAspectRatio = canvas.width / canvas.height;

      let renderableHeight, renderableWidth, xStart, yStart;

      if (canvasAspectRatio > imageAspectRatio) {
        const scale = canvas.width / image.width;
        renderableWidth = image.width;
        renderableHeight = canvas.height / scale;
        yStart = Math.abs((renderableHeight - image.height) / 2);
        xStart = 0;
      } else {
        const scale = canvas.height / image.height;
        renderableHeight = image.height;
        renderableWidth = canvas.width / scale;
        xStart = Math.abs((renderableWidth - image.width) / 2);
        yStart = 0;
      }

      ctx.drawImage(image, xStart, yStart, renderableWidth, renderableHeight, 0, 0, canvas.width, canvas.height);

      const detector = await detectorPromise;
      detector.setOptions({ runningMode: 'IMAGE' });
      const predictions = detector.detect(canvas);

      if (predictions.faceLandmarks.length === 0) return;

      const keypoints = predictions.faceLandmarks[0].map(({ x, y, z }) => ({
        x: x * canvas.width,
        y: y * canvas.height,
        z: z,
      }));

      setKeypoints(keypoints);

      setLogData({
        imageAspectRatio,
        canvasAspectRatio,
        imageWidth: image.width,
        imageHeight: image.height,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      });
    };
  }, []);

  useEffect(() => {
    if (!keypoints.length) return;

    /**
     * @type {HTMLCanvasElement}
     */
    const canvas = faceCanvasRef.current;
    canvas.width = Math.min(window.innerWidth, 0.7 * window.innerHeight);
    canvas.height = 0.7 * window.innerHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    switch (selectedHealth) {
      case 'brightness':
        ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
        ctx.lineWidth = 4;
        ctx.fillStyle = 'rgba(255, 248, 189, 0.2)';
        break;
      case 'darkSpots':
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        break;
      case 'poreSize':
        ctx.setLineDash([5, 5]);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.fillStyle = 'rgba(255, 255, 255, 0)';
        break;
      case 'texture':
        ctx.strokeStyle = 'rgba(131, 136, 244, 1)';
        ctx.lineWidth = 3;
        ctx.fillStyle = 'rgba(131, 136, 244, 0)';
        break;
    }

    const generator = d3
      .line()
      .x((d) => d.x)
      .y((d) => d.y)
      // .curve(d3.curveBundle.beta(1))
      .curve(d3.curveCatmullRomClosed.alpha(1))
      .context(ctx);

    for (const path of keypointsMap[selectedHealth]) {
      const data = path.map((index) => keypoints[index]);
      ctx.beginPath();
      generator(data);
      ctx.stroke();
      ctx.fill();
    }
  }, [keypoints, selectedHealth]);

  return (
    <div className="skin-report">
      {mode === 'historyView' && <Navbar pinned logo="" rightButtonIcon={null} />}
      <div className="image-container">
        <canvas className="face-canvas" ref={faceCanvasRef} />
        <img className="selfie-image" src={report?.imageUrls[selectedHealth]} />
        {/* {selectedHealth !== 'skinHealth' && selectedHealth !== 'acne' && (
          <img className="mask-image" src={maskMap[selectedHealth]} ref={maskRef} />
        )} */}
      </div>
      {isDebug && <div>{JSON.stringify(logData, null, 2)}</div>}
      <div className="report-title-bar">
        <div className="report-title">{content.reportTitle[lang]}</div>
        <ScoreCarousel selectedMetric={selectedHealth} setSelectedMetric={setSelectedHealth} scores={report?.scores} />
        <div className="report-subtitle">The higher the score, the better</div>
      </div>

      <Card title={content[selectedHealth][lang]} centerTitle>
        <ScoreSummary score={report?.scores[selectedHealth]} />
        <div className="score-comment">{content.scoreComment[selectedHealth][getScoreKey(report?.scores[selectedHealth])][lang]}</div>
      </Card>
      {selectedHealth === 'skinHealth' && (
        <>
          {!!report.focusAreas?.length && (
            <Card title={content.focusAreasTitle[lang]} centerTitle>
              <List items={focusAreaItems} indexed isTitleBold />
            </Card>
          )}

          {!!report.areasToMaintain?.length && (
            <Card title={content.areasToMaintainTitle[lang]} centerTitle>
              <List items={maintainAreaItems} indexed isTitleBold />
            </Card>
          )}
        </>
      )}
      {selectedHealth !== 'skinHealth' && (
        <>
          <Card centerTitle title={content.causeHeadings[selectedHealth][lang]}>
            <List indexed items={content.causes[selectedHealth][lang].map((cause) => ({ title: cause }))} />
          </Card>
          <Card
            centerTitle
            title={content.improvementHeadings[selectedHealth][lang]}
            overlay={!isLoggedIn}
            onOverlayButtonClick={() => emitLoginEvent(report.analysisId)}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <img src={Reduce} alt="reduce" style={{ width: '60%', alignSelf: 'center', marginBottom: '30px' }} />
            </div>
            <List indexed items={content.improvements[selectedHealth][lang].map((improvement) => ({ title: improvement }))} />
          </Card>
        </>
      )}
      <Button
        text={content.productRecommendation[lang]}
        onClick={() => {
          setAnalysisReport(report);
          navigate('/productRecommendation');
        }}
      />
      {mode !== 'offlineView' && (
        <div className="report-footer">
          <Button text="View all skin reports" variant="tertiary" icon={RightArrow} iconPosition="suffix" onClick={emitGetHistoryEvent} />
        </div>
      )}
      {mode === 'offlineView' && (
        <div className="report-footer">
          <Button text="Back to Home" variant="tertiary" icon={RightArrow} iconPosition="suffix" onClick={() => navigate('/landing')} />
        </div>
      )}
    </div>
  );
};

export default SkinCompleteDetail;
